<template>
  <Popup v-model="show" position="bottom">
    <div class="sku-popup">
      <div class="sku-popup__handle"></div>
      <div class="sku-popup__title-row">
        <div class="sku-popup__title">{{ $t('skuPop.chooseVariant') }}</div>
        <button type="button" class="sku-popup__close" @click="close" :aria-label="$t('cancel')">
          <span>×</span>
        </button>
      </div>
      <div class="sku-popup__divider"></div>
      <div class="sku-popup__header">
        <div class="sku-popup__img">
          <div class="sku-popup__img-wrapper" v-lazy:background-image="selectedImage"></div>
        </div>
        <div class="sku-popup__info">
          <div class="sku-popup__product-name">{{ product.name || $t('product') }}</div>
          <div class="sku-popup__selected">{{ selectedSkuStr }}</div>
        </div>
      </div>
      <div class="sku-popup__attr-list">
        <div class="sku-popup__attr-item" v-for="(spec, specIndex) in product.specs" :key="specIndex">
          <div class="attr-item__name">{{ spec.attrName }}</div>
          <div class="flex flex-wrap">
            <div class="attr-item__value" v-for="(value, valueIndex) in spec.values" :key="valueIndex" :class="{
              ac: selectedSpecs[spec.attrKey] == value,
              dis: isOptionDisabled(spec.attrKey, value)
            }" @click="toggleSpec(spec.attrKey, value)">
              {{ value }}
            </div>
          </div>
        </div>
      </div>
      <div class="sku-popup__divider"></div>
      <div class="sku-popup__footer">
        <div class="sku-popup__amount">
          <div class="sku-popup__amount-label">{{ $t('orderPage.firstRentalAmount') }}</div>
          <div class="sku-popup__amount-value">{{ priceText }}</div>
          <div v-if="selectedDeposit > 0" class="sku-popup__deposit">
            <span>{{ $t('orderPage.securityDeposit') }}</span>
            <strong>{{ depositText }}</strong>
          </div>
        </div>
        <div class="sku-popup__footer-divider"></div>
        <button type="button" class="sku-popup__selection" :class="{ disabled: !canConfirm }" @click="confirmSku">
          {{ $t('skuPop.selection') }}
        </button>
      </div>
    </div>
  </Popup>
</template>

<script>
import { Popup } from 'vant';
import { formatRupiahAmount } from '@/utils/money';
export default {
  name: 'SkuSelectPopup',
  components: {
    Popup
  },
  props: {
    product: {
      type: Object,
      default: () => ({
        id: '',
        name: '',
        price: 0, // 默认价格
        thumbnail: '',
        specs: [],
        skus: []
      })
    },
    preferredSkuId: {
      type: [String, Number],
      default: '',
    },
  },
  data() {
    return {
      show: false,
      selectedSpecs: {}, // 当前选择的规格，格式: { 颜色: '红色', 尺寸: 'M' }
      selectedSkuIdentity: '',
    }
  },
  computed: {
    // 当前匹配的SKU
    matchedSku() {
      const selectedValues = Object.values(this.selectedSpecs);
      if (selectedValues.length < Object.keys(this.product.specs).length) {
        return null; // 未选择完整规格
      }

      const matchedSkus = this.product.skus.filter(sku => {
        return this.product.specs.every(spec => {
          return sku.attributes[spec.attrKey] == this.selectedSpecs[spec.attrKey];
        });
      });
      return matchedSkus.find(sku => this.getSkuIdentity(sku) === this.selectedSkuIdentity)
        || matchedSkus[0]
        || null;
    },

    // 已选SKU的字符串表示
    selectedSkuStr() {
      if (!this.hasSelection) return this.$t('skuPop.noSelection');
      return this.product.specs.map(spec => {
        return this.selectedSpecs[spec.attrKey];
      }).filter(Boolean).join(' • ');
    },

    // 是否有选择
    hasSelection() {
      return Object.keys(this.selectedSpecs).length > 0;
    },

    // 是否选择了完整的SKU
    isSkuSelected() {
      return this.matchedSku !== null;
    },
    currentSku() {
      const selectedIdentity = this.selectedSkuIdentity;
      const selectedSku = selectedIdentity
        ? (this.product.skus || []).find(sku => this.getSkuIdentity(sku) === selectedIdentity)
        : null;
      return this.matchedSku
        || selectedSku
        || (this.product.skus || []).find(item => Number(item.stock) > 0)
        || (this.product.skus || [])[0]
        || null;
    },

    // 已选SKU的价格
    selectedPrice() {
      const sku = this.currentSku || {};
      const price = sku.price ?? this.product.price;
      const riskPrice = sku.riskPrice ?? this.product.riskPrice;
      return this.addMoney(price, riskPrice);
    },
    selectedDeposit() {
      const sku = this.currentSku || {};
      return Number(sku.depositPrice ?? this.product.depositPrice ?? 0) || 0;
    },
    priceText() {
      return this.selectedPrice > 0
        ? `${this.$t('priceUnit') || 'Rp'}${this.formatAmount(this.selectedPrice)}`
        : this.$t('productCommon.pricePending');
    },
    depositText() {
      return `${this.$t('priceUnit') || 'Rp'}${this.formatAmount(this.selectedDeposit)}`;
    },
    canConfirm() {
      return this.isSkuSelected && this.selectedStock > 0;
    },

    // 图片
    selectedImage() {
      return this.currentSku?.imageUrl || this.product.thumbnail || null;
    },


    // 已选SKU的库存
    selectedStock() {
      return this.matchedSku?.stock || 0;
    },

    // 计算所有可用的规格组合
    availableCombinations() {
      const combinations = [];
      const attrKeys = this.product.specs.map(spec => spec.attrKey);
      // 递归生成所有可能的组合
      const generateCombinations = (index, currentCombination) => {
        if (index == attrKeys.length) {
          // 检查这个组合是否有库存
          const matchingSku = this.product.skus.find(sku => {
            return attrKeys.every(attrKey =>
              sku.attributes[attrKey] == currentCombination[attrKey]
            );
          });
          if (matchingSku && matchingSku.stock > 0) {
            combinations.push({ ...currentCombination });
          }
          return;
        }

        const attrKey = attrKeys[index];
        this.product.specs[index].values.forEach(value => {
          const newCombination = { ...currentCombination, [attrKey]: value };
          generateCombinations(index + 1, newCombination);
        });
      };

      generateCombinations(0, {});
      return combinations;
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initializeDefaultSku();
    });
  },
  watch: {
    preferredSkuId() {
      this.$nextTick(() => this.initializeDefaultSku());
    },
    'product.id'() {
      this.$nextTick(() => this.initializeDefaultSku());
    },
  },
  methods: {
    addMoney(...amounts) {
      return amounts.reduce((total, amount) => total + Math.round((Number(amount) || 0) * 100), 0) / 100;
    },
    formatAmount(amount) {
      return formatRupiahAmount(amount);
    },
    open() {
      this.show = true;
    },

    initializeDefaultSku() {
      const skus = this.product.skus || [];
      const preferredId = this.preferredSkuId === null || this.preferredSkuId === undefined
        ? ''
        : String(this.preferredSkuId);
      const preferredSku = preferredId
        ? skus.find(item => this.getSkuIdentity(item) === preferredId && Number(item.stock) > 0)
        : null;
      const defaultSku = preferredSku || skus.find(item => Number(item.stock) > 0);
      if (!defaultSku) return;

      this.selectedSkuIdentity = this.getSkuIdentity(defaultSku);
      this.selectedSpecs = {};
      Object.keys(defaultSku.attributes || {}).forEach(attrKey => {
        this.$set(this.selectedSpecs, attrKey, defaultSku.attributes[attrKey]);
      });
      this.$emit('defaultChange', {
        sku: this.matchedSku || defaultSku,
        selectedSpecs: { ...this.selectedSpecs },
        selectedSkuStr: this.selectedSkuStr
      });
    },

    getSkuIdentity(sku = {}) {
      const id = sku.id ?? sku.goodsSkuId ?? '';
      return id === null || id === undefined ? '' : String(id);
    },

    close() {
      this.show = false;
    },
    // 切换规格选择
    toggleSpec(attrKey, specValue) {
      if (this.isOptionDisabled(attrKey, specValue)) {
        return;
      }
      this.selectedSkuIdentity = '';
      // 如果已经选择相同的值，则取消选择
      if (this.selectedSpecs[attrKey] == specValue) {
        this.$delete(this.selectedSpecs, attrKey);
      } else {
        this.$set(this.selectedSpecs, attrKey, specValue);
      }

      // 触发选择变更事件
      this.$emit('spec-change', {
        selectedSpecs: { ...this.selectedSpecs },
        matchedSku: this.matchedSku
      });
    },

    // 判断选项是否应该禁用（无库存的组合）
    isOptionDisabled(attrKey, specValue) {
      // 如果已经选择了这个选项，不禁用（允许取消）
      if (this.selectedSpecs[attrKey] == specValue) {
        return false;
      }

      // 构建临时的选择组合来检查是否有效
      const tempSelection = { ...this.selectedSpecs, [attrKey]: specValue };

      // 检查是否有任何SKU匹配这个临时组合且有库存
      const hasAvailableCombination = this.availableCombinations.some(combination => {
        return Object.keys(tempSelection).every(key =>
          combination[key] == tempSelection[key]
        );
      });

      return !hasAvailableCombination;
    },

    // 判断选项是否无货（即使组合存在但库存为0）
    isOptionOutOfStock(attrKey, specValue) {
      // 构建临时的选择组合
      const tempSelection = { ...this.selectedSpecs, [attrKey]: specValue };

      // 查找匹配的SKU
      const matchingSku = this.product.skus.find(sku => {
        return Object.keys(tempSelection).every(key =>
          sku.attributes[key] == tempSelection[key]
        );
      });

      // 如果有匹配的SKU且库存为0，则显示无货
      return matchingSku && matchingSku.stock == 0;
    },

    // 清除所有选择
    clearAllSelections() {
      this.selectedSpecs = {};
      this.selectedSkuIdentity = '';
      this.$emit('clear-selection');
    },

    confirmSku() {
      if (!this.isSkuSelected) {
        for (let i = 0; i < this.product.specs.length; i++) {
          let item = this.product.specs[i];
          if (!this.selectedSpecs[item.attrKey]) {
            this.$toast(this.$t('skuPop.noSelectAttr', {attrName: item.attrName }));
            break;
          }
        }
        return;
      }
      if (this.selectedStock <= 0) {
        this.$toast(this.$t('skuPop.noStock'));
        return;
      }
      if (this.canConfirm) {
        this.$emit('confirm', {
          sku: this.matchedSku,
          selectedSpecs: { ...this.selectedSpecs },
          selectedSkuStr: this.selectedSkuStr
        });
        this.close();
      }
    },
  }
}
</script>

<style lang="less" scoped>
.van-popup {
  background: none;
}

.sku-popup {
  width: 100%;
  max-width: 750px;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  background: #FFFFFF;
  border-radius: 32px 32px 0 0;
  filter: blur(0px);
  position: relative;
  padding: 12px 32px 18px;
  box-sizing: border-box;

  &__handle {
    width: 72px;
    height: 7px;
    border-radius: 999px;
    background: #DEE3EF;
    margin: 0 auto 20px;
  }

  &__title-row {
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__title {
    color: #020B3A;
    font-size: 34px;
    line-height: 42px;
    font-weight: 500;
  }

  &__close {
    width: 48px;
    height: 48px;
    border: 0;
    border-radius: 50%;
    background: #F4F6FB;
    color: #6F7482;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;

    span {
      font-size: 42px;
      line-height: 42px;
      font-weight: 300;
      transform: translateY(-2px);
    }
  }

  &__divider {
    height: 1px;
    background: #E1E5EF;
    margin: 20px 0;
    flex: 0 0 auto;
  }

  &__header {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
  }

  &__img-wrapper {
    width: 104px;
    height: 104px;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    border-radius: 12px;
    border: 1px solid #D6DCE8;
    overflow: hidden;
  }

  &__info {
    text-align: left;
    flex: 1;
    min-width: 0;
    padding-left: 24px;
  }

  &__product-name {
    color: #020B3A;
    font-size: 30px;
    line-height: 38px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__selected {
    margin-top: 10px;
    color: #697184;
    font-size: 24px;
    line-height: 32px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__attr-list {
    padding: 8px 10px 22px 0;
    margin: 0;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  &__attr-item {
    margin-top: 28px;

    &:first-child {
      margin-top: 0;
    }
  }

  &__footer {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    padding: 4px 0 14px;
  }

  &__amount {
    flex: 1.85;
    min-width: 0;
  }

  &__amount-label {
    color: #697184;
    font-size: 28px;
    line-height: 34px;
  }

  &__amount-value {
    margin-top: 4px;
    color: #0B45FF;
    font-size: 30px;
    line-height: 38px;
    font-weight: 400;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__deposit {
    margin-top: 6px;
    font-size: 28px;
    line-height: 34px;
    color: #697184;

    span,
    strong {
      display: block;
    }

    strong {
      color: #0B45FF;
      font-weight: 400;
    }
  }

  &__footer-divider {
    width: 1px;
    height: 92px;
    background: #E1E5EF;
    margin: 0 24px;
  }

  &__selection {
    flex: 1.35;
    min-width: 0;
    height: 92px;
    border: 0;
    border-radius: 18px;
    background: #0B45FF;
    color: #FFFFFF;
    font-size: 30px;
    line-height: 38px;
    font-weight: 500;

    &.disabled {
      opacity: 0.45;
    }
  }
}

.attr-item {
  &__name {
    text-align: left;
    font-size: 28px;
    color: #020B3A;
    line-height: 36px;
    font-weight: 500;
    margin-bottom: 16px;
  }

  &__value {
    min-width: 112px;
    height: 76px;
    box-sizing: border-box;
    padding: 0 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 16px;
    margin-bottom: 16px;
    font-size: 26px;
    line-height: 32px;
    color: #020B3A;
    background: #FFFFFF;
    border-radius: 12px;
    border: 1px solid #D6DCE8;
    cursor: pointer;

    &.dis {
      background: #F5F6FA;
      border-color: #DDE2EC;
      opacity: 0.4;
      color: #697184;
      cursor: not-allowed;
    }

    &.ac {
      color: #0B45FF;
      background: #F3F7FF;
      border: 3px solid #0B45FF;
      font-weight: 500;
    }
  }
}
</style>
